import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';
import { 
  Check, Crown, Star, TrendingUp, Sparkles, LogIn, 
  ArrowRight, ShieldCheck, Zap, Rocket, ZapIcon 
} from 'lucide-react';
import logo from "../assets/logo64.png";

interface Plan {
  plan_id: string;
  plan_name: string;
  description: string;
  duration: string;
  price?: number;
}

const SelectPlan: React.FC = () => {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [selectedPlan, setSelectedPlan] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => { loadPlans(); }, []);

  const loadPlans = async () => {
    try {
      const { data, error } = await supabase
        .from('Vitta_Plan')
        .select('*')
        .order('price', { ascending: true });
      if (error) throw error;
      setPlans(data || []);
    } catch (error: any) {
      toast.error('Failed to load plans');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPlan = async () => {
    if (!selectedPlan) {
      toast.error('Please select a plan');
      return;
    }
    setSubmitting(true);
    try {
      const regData = sessionStorage.getItem('registrationData');
      if (!regData) {
        toast.error('Registration data not found');
        navigate('/register');
        return;
      }
      const { name, email, phone } = JSON.parse(regData);
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email,
        password: Math.random().toString(36),
        options: { data: { name, phone } }
      });
      if (authError) throw authError;
      if (authData.user) {
        await supabase.from('user_profiles').insert({
          id: authData.user.id,
          name, email, phone,
          plan_id: selectedPlan,
        });
      }
      sessionStorage.removeItem('registrationData');
      toast.success('Registration successful!');
      await supabase.auth.signInWithOtp({ email });
      navigate('/login');
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const getPlanIcon = (index: number) => {
    const icons = [
      <Rocket className="text-white" size={28} />,
      <Crown className="text-white" size={28} />,
      <ZapIcon className="text-white" size={28} />
    ];
    return icons[index] || <Star className="text-white" size={28} />;
  };

  const getPlanGradient = (index: number) => {
    const gradients = [
      'from-blue-600 to-cyan-500',
      'from-accent-primary to-accent-secondary',
      'from-purple-600 to-pink-500'
    ];
    return gradients[index] || 'from-gray-600 to-gray-400';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#08090a] flex items-center justify-center">
        <div className="relative flex flex-col items-center">
          <div className="w-16 h-16 border-4 border-accent-primary/20 border-t-accent-primary rounded-full animate-spin"></div>
          <p className="mt-4 text-xs font-black tracking-[0.3em] text-gray-500 uppercase">Configuring Experience</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#08090a] text-white antialiased font-sans selection:bg-accent-primary/30">
      <div className="fixed inset-0 bg-[radial-gradient(circle_at_50%_0%,#1a1b23_0%,transparent_50%)] pointer-events-none" />
      <div className="fixed top-[-10%] left-[-10%] w-[40%] h-[40%] bg-accent-primary/5 rounded-full blur-[120px] pointer-events-none" />

      {/* --- NAVBAR --- */}
      <nav className="sticky top-0 z-50 backdrop-blur-2xl bg-black/40 border-b border-white/5 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          
          <div 
            onClick={() => navigate('/welcome')} 
            className="flex items-center gap-3 group cursor-pointer"
          >
            <div className="relative w-9 h-9">
              <div className="absolute inset-0 bg-orange-500 blur-md opacity-40 group-hover:opacity-100 transition-opacity"></div>
              <img src={logo} alt="V" className="relative w-full h-full object-cover rounded-lg border border-white/20" />
            </div>
            {/* LOGO ORANGE GRADIENT FIXED */}
            <span className="text-2xl font-black tracking-tighter italic bg-gradient-to-r from-white to-orange-500 bg-clip-text text-transparent">ytta</span>
          </div>

          <a href="/login" className="px-5 py-2 rounded-full border border-white/10 hover:bg-white/5 transition-all text-sm font-bold flex items-center gap-2">
            <LogIn size={14} /> Login
          </a>
        </div>
      </nav>

      <div className="max-w-6xl mx-auto px-6 pt-12 pb-24 relative z-10">
        {/* --- HEADER --- */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent-primary/10 border border-accent-primary/20 text-accent-primary text-[10px] font-black tracking-widest uppercase mb-4">
              Step 2 of 2: Choose Your Engine
          </div>
          <h1 className="text-4xl md:text-5xl font-black tracking-tighter mb-4 italic">
            Unlock <span className="bg-gradient-to-r from-white to-orange-500 bg-clip-text text-transparent">Professional Signals</span> 
          </h1>
          <p className="text-gray-500 text-sm max-w-lg mx-auto leading-relaxed">
            Choose the membership that aligns with your capital goals. All plans include institutional-grade market data.
          </p>
        </div>

        {/* --- PLANS GRID --- */}
        <div className="grid lg:grid-cols-3 gap-8 mb-16">
          {plans.map((plan, index) => (
            <div
              key={plan.plan_id}
              onClick={() => setSelectedPlan(plan.plan_id)}
              className={`group relative bg-white/[0.02] backdrop-blur-sm rounded-[2.5rem] p-8 border transition-all duration-500 cursor-pointer flex flex-col ${
                selectedPlan === plan.plan_id
                  ? 'border-accent-primary ring-1 ring-accent-primary/50 bg-white/[0.04] scale-[1.02] shadow-[0_20px_50px_rgba(0,0,0,0.5)]'
                  : 'border-white/5 hover:border-white/20'
              }`}
            >
              {index === 1 && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-20">
                  <div className="bg-gradient-to-r from-accent-primary to-accent-secondary px-6 py-1.5 rounded-full text-[10px] font-black shadow-xl animate-bounce">
                    ELITE CHOICE
                  </div>
                </div>
              )}

              <div className="mb-8">
                <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${getPlanGradient(index)} flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-500`}>
                  {getPlanIcon(index)}
                </div>
              </div>

              <div className="mb-8">
                {/* PLAN NAME ORANGE GRADIENT FIXED */}
                <h3 className="text-2xl font-black tracking-tight mb-2 uppercase italic bg-gradient-to-r from-white to-orange-500 bg-clip-text text-transparent">
                  {plan.plan_name}
                </h3>
                
                <div className="flex items-baseline gap-1">
                  {/* PRICE ORANGE GRADIENT FIXED */}
                  <span className="text-4xl font-black tracking-tighter bg-gradient-to-r from-white to-orange-500 bg-clip-text text-transparent">
                    ₹{plan.price}
                  </span>
                  <span className="text-gray-500 text-xs font-bold uppercase tracking-widest">/{plan.duration || '30'} days</span>
                </div>
                
                <p className="text-gray-500 text-xs mt-4 leading-relaxed font-medium">
                  {plan.description}
                </p>
              </div>

              <div className="space-y-4 mb-10 flex-grow">
                {[
                  'Institutional Signal Stream',
                  'Real-time Entry/Exit Alerts',
                  'Positional Swing Strategies',
                  'Priority Support Line'
                ].map((feature, fIdx) => (
                  <div key={fIdx} className="flex items-center gap-3">
                    <div className="flex-shrink-0 w-5 h-5 rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
                      <Check size={12} className="text-emerald-400" />
                    </div>
                    <span className="text-xs text-gray-400 font-medium">{feature}</span>
                  </div>
                ))}
              </div>

              <div className={`w-full py-4 rounded-2xl font-black text-[10px] tracking-[0.2em] uppercase transition-all flex items-center justify-center gap-2 ${
                selectedPlan === plan.plan_id 
                ? 'bg-accent-primary text-white shadow-lg shadow-accent-primary/20' 
                : 'bg-white/5 text-gray-500 group-hover:bg-white/10 group-hover:text-white'
              }`}>
                {selectedPlan === plan.plan_id ? <><Sparkles size={14}/> Selected</> : 'Choose Plan'}
              </div>
            </div>
          ))}
        </div>

        {/* --- CONTINUE ACTION --- */}
        <div className="max-w-md mx-auto text-center">
          <button
            onClick={handleSelectPlan}
            disabled={!selectedPlan || submitting}
            className="group w-full py-5 bg-white text-black hover:bg-accent-primary hover:text-white rounded-[2rem] font-black text-xs tracking-[0.3em] uppercase transition-all duration-500 disabled:opacity-20 flex items-center justify-center gap-3 shadow-2xl shadow-black"
          >
            {submitting ? (
              <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
            ) : (
              <>Initialize Dashboard <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" /></>
            )}
          </button>

          <button
            onClick={() => navigate('/register')}
            className="mt-8 text-[10px] font-black text-gray-600 hover:text-white uppercase tracking-[0.2em] transition-colors"
          >
            ← Back to Credentials
          </button>
        </div>
        
        {/* --- TRUST STATS --- */}
        <div className="mt-24 pt-12 border-t border-white/5">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-8 text-center">
            <div className="space-y-1">
              <div className="flex items-center justify-center gap-2 text-accent-primary">
                <ShieldCheck size={18} />
                <span className="text-2xl font-black tracking-tighter">10K+</span>
              </div>
              <p className="text-[9px] font-black text-gray-600 uppercase tracking-widest">Active Traders</p>
            </div>
            <div className="space-y-1">
              <div className="flex items-center justify-center gap-2 text-emerald-400">
                <TrendingUp size={18} />
                <span className="text-2xl font-black tracking-tighter">95%</span>
              </div>
              <p className="text-[9px] font-black text-gray-600 uppercase tracking-widest">Signal Accuracy</p>
            </div>
            <div className="hidden md:block space-y-1">
              <div className="flex items-center justify-center gap-2 text-amber-400">
                <Zap size={18} />
                <span className="text-2xl font-black tracking-tighter">24/7</span>
              </div>
              <p className="text-[9px] font-black text-gray-600 uppercase tracking-widest">Expert Support</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SelectPlan;